import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search } from 'lucide-react';
import { formatMoney, type CurrencyCode } from '@tengesa/shared';
import { api } from '../../lib/api';
import { useSession } from '../../app/session';
import { Button, Dialog, Spinner, TextInput } from '../../ui';
import { useDebounced } from '../../ui/inputs';
import { CustomerDialog, type Customer } from './CustomersPage';

export interface PickedCustomer {
  id: string;
  name: string;
  phone: string | null;
  balanceCents: number;
  creditLimitCents: number;
  availableCreditCents: number;
}

/** Find (or quickly add) the customer a sale is for. */
export function CustomerPicker({ onPick, onClose }: { onPick: (c: PickedCustomer) => void; onClose: () => void }) {
  const { me, can } = useSession();
  const cur = me.business.baseCurrency as CurrencyCode;
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const dq = useDebounced(q.trim(), 200);
  const res = useQuery({ queryKey: ['customers', dq], queryFn: () => api.get<Customer[]>(`/api/customers?q=${encodeURIComponent(dq)}`) });
  return (
    <Dialog
      open
      onClose={onClose}
      title="Who is the customer?"
      description="Needed for selling on account; optional otherwise (it puts the sale on their history)."
      footer={can('customers.manage') ? <Button icon={<Plus className="size-4" />} onClick={() => setAdding(true)}>New customer</Button> : undefined}
    >
      <div className="relative mb-3">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-faint" />
        <TextInput autoFocus aria-label="Search customers" className="pl-9" placeholder="Name or phone number" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {res.isLoading && <Spinner />}
      <ul className="max-h-80 divide-y divide-line overflow-y-auto rounded-[8px] border border-line">
        {res.data?.map((c) => (
          <li key={c.id}>
            <button className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-ledger-50" onClick={() => onPick(c)}>
              <span className="min-w-0">
                <span className="block truncate font-medium">{c.name}</span>
                <span className="block text-xs text-muted">{c.phone ?? 'No phone'}</span>
              </span>
              <span className="text-right text-sm">
                <span className="block num">Owes {formatMoney(c.balanceCents, cur)}</span>
                {c.creditLimitCents > 0 && <span className="block text-xs text-muted">Limit {formatMoney(c.creditLimitCents, cur)}</span>}
              </span>
            </button>
          </li>
        ))}
        {res.data?.length === 0 && <li className="px-3 py-4 text-muted">No customers match.</li>}
      </ul>
      {adding && <CustomerDialog customer={null} onClose={() => setAdding(false)} onSaved={(c) => onPick({ ...c, balanceCents: 0, availableCreditCents: c.creditLimitCents })} />}
    </Dialog>
  );
}
